import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Alert, Box, CircularProgress } from '@mui/material';
import QuestionnaireService from '../../../core/services/questionnaire.service';
import { ContainerSummary, TopSummary } from './payment.style';
import CheckoutWidget from './widgets/checkout.widget';
import AppHelper from '../../../core/helpers/AppHelper';
import UpdatePaymentMethodWidget from '../../membership/updatePaymentMethod.widget';

const questionnaireService = new QuestionnaireService();
const stripePromise = loadStripe(
  // eslint-disable-next-line no-undef
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
);

const PaymentyPage = ({ businessId, isEditMode, openDialog, setLoader }) => {
  const [paymentType, setPaymentType] = useState('card');
  const [checkoutError, setCheckoutError] = useState(null);
  const [options, setOptions] = useState({
    clientSecret: '',
  });
  const setupIntentRequestId = useRef(0);

  const _getStripeIntent = useCallback(async () => {
    const requestId = setupIntentRequestId.current + 1;
    setupIntentRequestId.current = requestId;

    try {
      setOptions({ clientSecret: '' });
      const r = await questionnaireService.createSetupIntent(paymentType);
      if (r && requestId === setupIntentRequestId.current) {
        setOptions((options) => ({
          ...options,
          clientSecret: r.data.clientSecret,
        }));
      }
    } catch (e) {
      if (requestId === setupIntentRequestId.current) {
        setCheckoutError((currentError) => currentError || AppHelper.getError(e));
      }
    }
  }, [paymentType]);

  const _reloadStripeIntent = async () => {
    await _getStripeIntent();
  };

  const handlePaymentTypeChange = useCallback((nextPaymentType) => {
    setupIntentRequestId.current += 1;
    setCheckoutError(null);
    setOptions({ clientSecret: '' });
    setPaymentType(nextPaymentType);
  }, []);

  const _init = useCallback(async () => {
    await _getStripeIntent();
  }, [_getStripeIntent]);

  useEffect(() => {
    (async function init() {
      await _init();
    })();
  }, [_init]);

  return (
    <>
      <ContainerSummary>
        <TopSummary>PAYMENT METHOD</TopSummary>
        {checkoutError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {checkoutError}
          </Alert>
        )}
        {options.clientSecret !== '' ? (
          <Elements stripe={stripePromise} options={options}>
            {isEditMode ? (
              <UpdatePaymentMethodWidget
                clientSecret={options.clientSecret}
                reloadClienteSecret={_reloadStripeIntent}
                openDialog={openDialog}
                setReload={setLoader}
                paymentType={paymentType}
                setPaymentType={handlePaymentTypeChange}
                businessId={businessId}
                setCheckoutError={setCheckoutError}
              />
            ) : (
              <CheckoutWidget
                clientSecret={options.clientSecret}
                reloadClienteSecret={_reloadStripeIntent}
                paymentType={paymentType}
                setPaymentType={handlePaymentTypeChange}
                setCheckoutError={setCheckoutError}
              />
            )}
          </Elements>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
      </ContainerSummary>
    </>
  );
};

export default PaymentyPage;

PaymentyPage.defaultProps = {
  businessId: null,
  isEditMode: false,
};

PaymentyPage.propTypes = {
  businessId: PropTypes.number,
  isEditMode: PropTypes.bool,
  openDialog: PropTypes.func.isRequired,
  setLoader: PropTypes.func.isRequired,
};
